import { Component, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '../../../auth/auth.service';
import { UserDto } from '../../../auth/user.dto';
import { DisplayLabel } from '../../../shared/models/displayLabel';
import { DisplayStructure } from '../../../shared/models/displayStructure';
import { SupportTypes } from '../../../shared/models/enums/support-type.enum';
import { BeneficiariesService } from '../../../shared/services/beneficiaries.service';
import { StructureService } from '../../../shared/services/structure.service';
import { SupportTypeService } from '../../../shared/services/supportType.service';
import { ToastService } from '../../../shared/toast/toast.service';
import { DateValidators } from '../../../shared/validators/date.validators';
import { SummaryExportEntryDto } from './summary-export-entry.dto';

@Component({
  selector: 'app-beneficiary-summary-export',
  templateUrl: './beneficiary-summary-export.component.html',
  standalone: false,
})
export class BeneficiarySummaryExportComponent implements OnInit {
  public user: UserDto | undefined = undefined;

  private beneficiaryId: number;
  public form: UntypedFormGroup;
  public submitted: boolean = false;
  public structures: DisplayStructure[] = [];
  public supportTypes: (DisplayLabel & { category?: string })[] = [];

  constructor(
    private readonly authService: AuthService,
    private readonly beneficiariesService: BeneficiariesService,
    private readonly structureService: StructureService,
    private readonly supportTypesService: SupportTypeService,
    private readonly toastService: ToastService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly modal: NgbActiveModal,
  ) {}

  public async ngOnInit(): Promise<void> {
    this.loadForm();
    await this.initLists();
  }

  public onInit(beneficiaryId: number): void {
    this.beneficiaryId = beneficiaryId;
  }

  public async onSubmit(): Promise<void> {
    this.submitted = true;

    if (this.form.valid) {
      const summaryExportEntryDto: SummaryExportEntryDto = {
        beginDate: this.form.controls.beginDate.value,
        endDate: this.form.controls.endDate.value,
        structureId: this.form.controls.structureId.value,
        supportTypeIds: this.form.controls.supportTypeIds.value,
      };
      try {
        await this.beneficiariesService.exportSummary(this.beneficiaryId, summaryExportEntryDto);
      } catch (error) {
        if (error.status === 400) {
          this.toastService.show('Aucune donnée ne correspond. Vérifiez les paramètres de votre recherche.', 'danger');
        }
      }
    }
  }

  public cancel(): void {
    this.modal.close();
  }

  private loadForm(): void {
    this.form = this.formBuilder.group(
      {
        beginDate: new FormControl<Date>(undefined, [DateValidators.dateFormatValidator, Validators.required]),
        endDate: new FormControl<Date>(undefined, [DateValidators.dateFormatValidator, Validators.required]),
        structureId: [undefined, Validators.required],
        supportTypeIds: [undefined],
      },
      {
        validators: [DateValidators.datesOrderValidator('beginDate', 'endDate')],
      },
    );
  }

  private async initLists(): Promise<void> {
    this.user = await this.authService.getUser();
    this.structures = await this.structureService.findAllInBeneficiarySupportArea();
    if (this.user?.groups?.includes('STRUCTURE')) {
      this.form.controls.structureId.setValue(this.user.structureId);
      this.form.controls.structureId.disable();
    }
    this.supportTypes = (await this.supportTypesService.findAll())
      .filter((supportType) => supportType.id !== SupportTypes.ITE_ID_PLIE && supportType.id !== SupportTypes.HORS_ACCOMPAGNEMENT)
      .map((value: DisplayLabel) => ({
        category: 'Tous',
        ...value,
      }));
  }
}
